/**
 * Structure-Wall Corner Adjustment
 *
 * Invariants this file is committed to (see
 * `claude-docs/2026-05-29-ceiling-region-partitioning.md`):
 *
 *   1. The snap pass mutates only Y. It never writes
 *      `polygonCorners[*].x` / `.z`, `alignment.bottomLeft.{x,z}`,
 *      or `alignment.bottomRight.{x,z}`. Verified by a grep:
 *      `wall.polygonCorners[idx].y = targetY` at :1267 is the
 *      only `.y =` site that touches polygon corners; no
 *      `.x =` / `.z =` / `.bottomLeft =` writes exist.
 *   2. The snap pass skips any polygon corner whose
 *      `_regionAssigned` flag is set. Those Ys are owned by the
 *      region-fit / nudge phases (Phases 4 and 6 of the ceiling
 *      region-partitioning pipeline) and snap must leave them
 *      alone — otherwise per-region ceiling fits get clobbered
 *      by best-effort corner-Y agreement. The skip is a
 *      one-line early return in `trySnapPolygonCornerAtEnd`
 *      after the `idx` lookup; `snapOneAlignmentWall` inherits
 *      the same behavior via its call to that function.
 *
 * After the CV polygon build produces room polygons with mitered
 * corners, each structure wall's length and center may not match the
 * polygon corners exactly — a shift of an exterior wall's center, an
 * earlier adjustment from a neighboring corner, or an imprecise input
 * can all leave the wall's *surface corner* at the near end misaligned
 * with the room polygon's corner (which is itself derived from the
 * intersection of adjacent walls' surface offset lines).
 *
 * This pass walks every graph node (once) and enforces exact
 * alignment — wall surface corners match polygon corners to
 * floating-point precision — by lengthening or shortening each wall's
 * near-corner end along its own direction. It does **not** shift the
 * centerline perpendicular to the wall.
 *
 * Terminology. We name corner shapes by their **interior angle
 * value**, avoiding the ambiguous "convex/concave" labels which mean
 * opposite things depending on whether you read them as polygon math,
 * viewer-from-inside, or building exterior.
 *
 *  - **Right-angle** corner: interior angle ≈ 90°. A typical
 *    rectangular room corner.
 *  - **Reflex** corner: interior angle ≈ 270°. The inner elbow of an
 *    L-shaped room — the wall material juts into the room here.
 *  - **Oblique** corner: anything else (non-right angle).
 *
 * Per-node rules:
 *
 *  - **Degree 2, right-angle**: exactly one wall ends at the polygon
 *    corner; the other **extends past** the polygon corner by one
 *    thickness so the corner region (thickness × thickness) is filled.
 *  - **Degree 2, reflex**: one wall ends at the polygon corner; the
 *    other **shrinks away** from the corner by one thickness so the
 *    two walls don't overlap past the elbow tip.
 *  - **Degree 2, oblique**: both walls end exactly at the polygon
 *    corner.
 *  - **Degree 3 T-junction, classic (one continuing + one stem)**:
 *    processed only from the stem's side view. The stem's near-end
 *    aligns with the polygon corner; the continuing wall is left
 *    untouched. The polygon view where both edges trace the
 *    continuing wall's id is the straight-through view — skipped.
 *  - **Degree 3 T-junction, three distinct walls**: two are collinear
 *    and one is the perpendicular stem. The stem aligns with the
 *    polygon corner; the two collinear walls are joined at one face
 *    of the stem (the stopper retreats to that face, the extender
 *    extends to that face from the other side) — no overlap, no gap.
 *  - **Degree 4 (cross junction)**: skipped. Four walls terminate at
 *    the shared node by construction.
 *
 * Each graph node is processed at most once across all rooms — the
 * first polygon that references the node decides its fate.
 *
 * When picking "ends at" vs. "adjust" for degree-2 right-angle and
 * reflex corners: if one wall is already ending at the polygon corner
 * (within floating-point precision), pick that wall — avoids
 * unnecessary movement. Otherwise, pick the right-side wall
 * arbitrarily.
 */

const {
  getWallDirection,
  getWallNormal,
  calculateWallCorners,
  intersectLinesXZ,
  THIN_THRESHOLD,
} = require('./wallGeometry');
const { DEFAULT_WALL_THICKNESS } = require('./wallConstants');
const { outlineEdges } = require('./wallOutlineEdges');

// Per-wall-type snap tolerances for snapWallHeightsAtCorners.
//   - Rectangular walls lift the whole top edge, so the tolerance must
//     be tight (5 cm). Anything larger is real architecture, not noise.
//   - PolygonCorners walls update one corner's Y while the rest of the
//     top boundary decays back linearly; the gate is on the *displaced
//     proportion*, not on the raw delta. See snapWallHeightsAtCorners.
const SNAP_TOL_RECT = 0.05;
const POLY_NEAR_LIMIT = 0.1; // 10 cm — threshold for "meaningful displacement"
const POLY_DISPLACED_PROPORTION_MAX = 0.75; // ≤ 75% displaced is acceptable
// Absolute ceiling on the per-corner lift. Beyond this, the height
// difference at the corner is treated as architectural (a real step
// where a vault or perpendicular soffit meets the wall's flat top)
// rather than alignment drift — snapping would force the rest of the
// wall to slope toward the lifted corner across its full length, which
// the user has explicitly flagged as wrong on long walls (e.g. the
// 7BDBE05A bedroom's north wall picked up a 25 cm lift from a 2.36 m
// perpendicular vault at its NE corner; the resulting slope across
// the 3.4 m wall length was visible in the ceiling debug page while
// the parallel room wall stayed flat). 15 cm comfortably accepts
// alignment-drift snaps (e.g. the 11 cm 8469 case) while rejecting
// architectural steps.
const POLY_SNAP_MAX_DELTA = 0.15;

// Exact-match epsilon for floating-point comparisons. Floor polygon
// corners are algebraically derived from wall surface-line
// intersections, so a correctly-placed wall's surface corner should
// match to machine precision. Anything larger is a real mismatch.
const PRECISION_EPS = 1e-6;

// Tolerance for the "right-angle" classification of an interior
// polygon angle. 2° is generous enough to accommodate rotation drift
// from earlier alignment steps while still excluding genuine
// non-orthogonal corners.
const RIGHT_ANGLE_DEG_TOL = 2;

// Guard against very-acute / near-collinear corners. miter = π/2 −
// interior/2, so 80° corresponds to interior ≈ 20°. Below this the
// back-surface intersection diverges and the trapezoid is no longer
// a useful approximation — skip the miter and fall back to today's
// "snap both ends to polygon corner" behavior.
const MAX_MITER_ANGLE_DEG = 80;
const MAX_MITER_ANGLE_RAD = (MAX_MITER_ANGLE_DEG * Math.PI) / 180;

// ============================================================================
// Wall primitives
// ============================================================================

/**
 * From a list of candidate structure-wall ids backing one polygon
 * edge, pick the wall whose nearest centerline endpoint sits
 * closest to the given corner. Used at corners of polygon edges
 * that absorbed multiple collinear structure walls during room-wall
 * merging — the corner may sit at the end of a short stub absorbed
 * into a longer wall, not at either end of the longer wall itself.
 *
 * Returns the picked id, or null if no candidates resolve.
 */
function pickWallAtCorner(candidateIds, wallById, corner) {
  let bestId = null;
  let bestDist = Infinity;
  for (const id of candidateIds || []) {
    const w = wallById.get(id);
    if (!w) continue;
    const corners = calculateWallCorners(w);
    if (!corners) continue;
    const dBL = Math.hypot(
      corners.bottomLeft.x - corner.x,
      corners.bottomLeft.z - corner.z
    );
    const dBR = Math.hypot(
      corners.bottomRight.x - corner.x,
      corners.bottomRight.z - corner.z
    );
    const d = Math.min(dBL, dBR);
    if (d < bestDist) {
      bestDist = d;
      bestId = id;
    }
  }
  return bestId;
}

/**
 * Which end of a wall (BL or BR) has its centerline endpoint closer
 * to the given point?
 */
function nearestWallEnd(wall, point) {
  const corners = calculateWallCorners(wall);
  if (!corners) return 'BL';
  const dBL = Math.hypot(
    corners.bottomLeft.x - point.x,
    corners.bottomLeft.z - point.z
  );
  const dBR = Math.hypot(
    corners.bottomRight.x - point.x,
    corners.bottomRight.z - point.z
  );
  return dBL <= dBR ? 'BL' : 'BR';
}

/**
 * Centerline endpoint position of a wall at the specified end.
 */
function centerlineEndpoint(wall, end) {
  const dir = getWallDirection(wall);
  const hl = (wall.dimensions?.x || 0) / 2;
  return end === 'BL'
    ? {
        x: wall.center.x - hl * dir.x,
        y: wall.center.y,
        z: wall.center.z - hl * dir.z,
      }
    : {
        x: wall.center.x + hl * dir.x,
        y: wall.center.y,
        z: wall.center.z + hl * dir.z,
      };
}

/**
 * Project `point` onto the line through `wall.center` parallel to
 * `wall`'s direction. Returns a point on the wall's centerline with
 * zero perpendicular offset from the line.
 */
function projectOntoCenterline(point, wall) {
  const dir = getWallDirection(wall);
  const dx = point.x - wall.center.x;
  const dz = point.z - wall.center.z;
  const along = dx * dir.x + dz * dir.z;
  return {
    x: wall.center.x + along * dir.x,
    y: wall.center.y,
    z: wall.center.z + along * dir.z,
  };
}

/**
 * Move the `end` (BL or BR) of `wall` to `target` along the wall's
 * own direction. `target` must lie on the wall's centerline; any
 * perpendicular component is ignored. Keeps the opposite end fixed;
 * updates `wall.center`, `wall.dimensions.x`, and moves any openings
 * parented to this wall by the resulting center shift.
 *
 * Back-to-back composites carry per-end boundary flags
 * (`_backToBackBoundary.{start,end}`): the boundary endpoint is the
 * join with the non-composite partner (e.g., Montrose composite's
 * south end meeting the partition tail) and must NOT be moved here,
 * or the overlap-region thickness break drifts off the partner. The
 * non-boundary end stays adjustable so the composite can meet
 * perpendicular walls at the open end like any other wall.
 */
function moveWallEndAlong(wall, end, target, openings) {
  // Back-to-back composite boundary endpoints are precisely positioned
  // at the overlap-region bound where the non-composite partner takes
  // over. Moving them would drift the boundary off the partner. The
  // FREE end (no kept tail abutting) can still be adjusted — typically
  // extended past a perpendicular wall's centerline to its outer
  // surface via `applyOffsetPastCorner`.
  if (wall._backToBackComposite) {
    const boundaryKey = end === 'BL' ? 'start' : 'end';
    if (wall._backToBackBoundary?.[boundaryKey]) return 0;
  }
  const dir = getWallDirection(wall);
  const curEnd = centerlineEndpoint(wall, end);
  const alongDelta =
    (target.x - curEnd.x) * dir.x + (target.z - curEnd.z) * dir.z;
  if (Math.abs(alongDelta) < PRECISION_EPS) return 0;
  // For BL: moving along +dir *shrinks* the wall (BL moves toward
  // center); moving along -dir extends. For BR: opposite. Map
  // alongDelta → signed length change accordingly.
  const deltaLen = end === 'BL' ? -alongDelta : alongDelta;
  const absShift = Math.abs(deltaLen) / 2;
  const signFromEnd = end === 'BL' ? -1 : +1;
  const signFromOp = deltaLen >= 0 ? +1 : -1;
  const s = signFromEnd * signFromOp;
  const dx = s * absShift * dir.x;
  const dy = s * absShift * dir.y;
  const dz = s * absShift * dir.z;
  wall.center.x += dx;
  wall.center.y += dy;
  wall.center.z += dz;
  wall.dimensions.x += deltaLen;
  moveAssociatedOpenings(wall.id, dx, dy, dz, openings);
  return alongDelta;
}

function moveAssociatedOpenings(wallId, dx, dy, dz, openings) {
  for (const arr of [openings.doors, openings.windows, openings.openings]) {
    if (!arr) continue;
    for (const e of arr) {
      if (e.parentWallIds?.[0] === wallId && e.center) {
        e.center.x += dx;
        e.center.y += dy;
        e.center.z += dz;
      }
    }
  }
}

// ============================================================================
// Polygon corner classification
// ============================================================================

/**
 * Classify the interior angle at polygon[i]. Returns one of:
 *   - `'right-angle'` : interior angle ≈ 90°  (rectangular room corner)
 *   - `'reflex'`      : interior angle ≈ 270° (inner elbow of an L-shape)
 *   - `'oblique'`     : anything else (non-right angle)
 * Assumes the polygon is traced CCW in z-down coords (interior on
 * left of traversal, negative signed area — the convention from
 * `room-polygon-builder.traceFace`). In that convention, a positive
 * signed cross of (pPrev−p) × (pNext−p) means interior < 180°; a
 * negative cross means reflex.
 */
function classifyCorner(polygon, i) {
  const N = polygon.length;
  const pPrev = polygon[(i - 1 + N) % N];
  const p = polygon[i];
  const pNext = polygon[(i + 1) % N];
  const v1x = pPrev.x - p.x;
  const v1z = pPrev.z - p.z;
  const v2x = pNext.x - p.x;
  const v2z = pNext.z - p.z;
  const len1 = Math.hypot(v1x, v1z);
  const len2 = Math.hypot(v2x, v2z);
  if (len1 < PRECISION_EPS || len2 < PRECISION_EPS) return 'oblique';
  const dot = v1x * v2x + v1z * v2z;
  const cos = Math.max(-1, Math.min(1, dot / (len1 * len2)));
  const unsigned = Math.acos(cos); // in [0, π]
  const cross = v1x * v2z - v1z * v2x;
  const interior = cross > 0 ? unsigned : 2 * Math.PI - unsigned;
  const tol = (RIGHT_ANGLE_DEG_TOL * Math.PI) / 180;
  if (Math.abs(interior - Math.PI / 2) < tol) return 'right-angle';
  if (Math.abs(interior - (3 * Math.PI) / 2) < tol) return 'reflex';
  return 'oblique';
}

// ============================================================================
// Graph node lookup and helpers
// ============================================================================

/**
 * For each wallId, the set of graph node indices touched by any edge
 * carrying that wallId. A wall split by a T- or cross-junction has
 * its id on multiple edges.
 */
function buildNodesByWall(graph) {
  const map = new Map();
  for (const e of graph.edges) {
    let s = map.get(e.wallId);
    if (!s) {
      s = new Set();
      map.set(e.wallId, s);
    }
    s.add(e.startNode);
    s.add(e.endNode);
  }
  return map;
}

/**
 * The graph node shared by both wall ids, or null. The polygon corner
 * between two consecutive polygon edges corresponds to this node —
 * both edges terminate at the same node in the planar graph.
 */
function findSharedNode(nodesByWall, wallIdA, wallIdB) {
  const a = nodesByWall.get(wallIdA);
  const b = nodesByWall.get(wallIdB);
  if (!a || !b) return null;
  for (const n of a) if (b.has(n)) return n;
  return null;
}

/**
 * Edge-count per wallId at the given node. Used to tell the stem
 * wall (1 incident edge) from the continuing wall (2 incident edges,
 * one on each side of the junction) at a degree-3 T-junction.
 */
function edgesPerWallAtNode(graph, nodeIdx) {
  const counts = new Map();
  for (const ei of graph.nodes[nodeIdx].edgeIndices) {
    const wid = graph.edges[ei].wallId;
    counts.set(wid, (counts.get(wid) || 0) + 1);
  }
  return counts;
}

// ============================================================================
// Per-node adjustments
// ============================================================================

function applyEndsAtCorner(wall, end, polygonCorner, openings) {
  const target = projectOntoCenterline(polygonCorner, wall);
  return moveWallEndAlong(wall, end, target, openings);
}

function applyOffsetPastCorner(
  wall,
  end,
  polygonCorner,
  signedOffset,
  openings
) {
  // signedOffset > 0 → extend past the corner by this distance along
  // the outward direction (from wall center toward the near end).
  // signedOffset < 0 → fall short by |signedOffset| in the same sense.
  const dir = getWallDirection(wall);
  const outX = end === 'BL' ? -dir.x : dir.x;
  const outZ = end === 'BL' ? -dir.z : dir.z;
  const projOnCenterline = projectOntoCenterline(polygonCorner, wall);
  const target = {
    x: projOnCenterline.x + signedOffset * outX,
    y: projOnCenterline.y,
    z: projOnCenterline.z + signedOffset * outZ,
  };
  return moveWallEndAlong(wall, end, target, openings);
}

function isEndAlreadyAtCorner(wall, end, polygonCorner) {
  const target = projectOntoCenterline(polygonCorner, wall);
  const curEnd = centerlineEndpoint(wall, end);
  return Math.hypot(target.x - curEnd.x, target.z - curEnd.z) < PRECISION_EPS;
}

/**
 * Look up `facesSameDirection` for a structure wall in a room from the
 * room's processingData cache. Returns null if the cache is missing,
 * which forces the oblique-corner code to fall back to no miter.
 */
function getFacesSameDirection(room, wallId) {
  return room?.processingData?.structureWalls?.[wallId]?.facesSameDirection;
}

/**
 * Compute the back-surface intersection point at an oblique corner
 * and store signed `leftMiter` / `rightMiter` on each wall.
 *
 * Geometry: the front face of each wall coincides with the polygon
 * edge, so both walls' front-face corners at this end are exactly
 * `polygonCorner`. The back face is the front face translated by
 * `-frontSign · normal · thickness`. We intersect the two walls' back
 * lines to find `B` — the back-face shared corner.
 *
 * Sign of the miter: with `alongOffset = (B − C) · d_wall` (signed
 * distance from C to B along the wall's centerline direction),
 *   - end='BR' →   miter =  atan(alongOffset / thickness)
 *   - end='BL' →   miter = -atan(alongOffset / thickness)
 * which yields positive miter when the back face is longer than the
 * front face at that end (non-reflex corners) and negative when the
 * front face is longer (reflex corners).
 *
 * Returns true if miters were written; false if any precondition
 * failed (caller should fall back to today's snap-only behavior).
 */
function tryWriteObliqueMiters(
  room,
  rightSw,
  rightEnd,
  leftSw,
  leftEnd,
  polygonCorner,
  stats,
  fsdStamped
) {
  const rightFsd = getFacesSameDirection(room, rightSw.id);
  const leftFsd = getFacesSameDirection(room, leftSw.id);
  if (rightFsd == null || leftFsd == null) return false;

  const rightT = rightSw.dimensions?.z || 0;
  const leftT = leftSw.dimensions?.z || 0;
  if (rightT < THIN_THRESHOLD || leftT < THIN_THRESHOLD) return false;

  const rightDir = getWallDirection(rightSw);
  const leftDir = getWallDirection(leftSw);
  const rightN = getWallNormal(rightSw);
  const leftN = getWallNormal(leftSw);

  // Each back-surface line: passes through (C - frontSign · n · t),
  // direction = wall direction d.
  const rightBackPt = {
    x: polygonCorner.x - (rightFsd ? +1 : -1) * rightN.x * rightT,
    y: polygonCorner.y,
    z: polygonCorner.z - (rightFsd ? +1 : -1) * rightN.z * rightT,
  };
  const leftBackPt = {
    x: polygonCorner.x - (leftFsd ? +1 : -1) * leftN.x * leftT,
    y: polygonCorner.y,
    z: polygonCorner.z - (leftFsd ? +1 : -1) * leftN.z * leftT,
  };
  const B = intersectLinesXZ(
    rightBackPt,
    {
      x: rightBackPt.x + rightDir.x,
      y: rightBackPt.y,
      z: rightBackPt.z + rightDir.z,
    },
    leftBackPt,
    {
      x: leftBackPt.x + leftDir.x,
      y: leftBackPt.y,
      z: leftBackPt.z + leftDir.z,
    }
  );
  if (!B) return false;

  const rightAlong =
    (B.x - polygonCorner.x) * rightDir.x + (B.z - polygonCorner.z) * rightDir.z;
  const leftAlong =
    (B.x - polygonCorner.x) * leftDir.x + (B.z - polygonCorner.z) * leftDir.z;

  const rightMiter = Math.atan(
    ((rightEnd === 'BR' ? +1 : -1) * rightAlong) / rightT
  );
  const leftMiter = Math.atan(
    ((leftEnd === 'BR' ? +1 : -1) * leftAlong) / leftT
  );

  if (
    Math.abs(rightMiter) > MAX_MITER_ANGLE_RAD ||
    Math.abs(leftMiter) > MAX_MITER_ANGLE_RAD
  ) {
    console.warn(
      `[adjustStructureWallCorners] miter angle exceeded ` +
        `${MAX_MITER_ANGLE_DEG}° (rightSw=${rightSw.id} ` +
        `${((rightMiter * 180) / Math.PI).toFixed(1)}°, ` +
        `leftSw=${leftSw.id} ${((leftMiter * 180) / Math.PI).toFixed(1)}°) ` +
        `at corner (${polygonCorner.x.toFixed(3)}, ${polygonCorner.z.toFixed(3)}); ` +
        `falling back to no miter.`
    );
    return false;
  }

  if (
    !writeMiterAtEnd(rightSw, rightEnd, rightMiter, rightFsd, fsdStamped) ||
    !writeMiterAtEnd(leftSw, leftEnd, leftMiter, leftFsd, fsdStamped)
  ) {
    return false;
  }
  stats.miteredCorners++;
  return true;
}

/**
 * Write the signed miter to `wall.leftMiter` or `wall.rightMiter`
 * depending on which end is at the corner. Also records
 * `frontFacesSameDirection` so consumers can interpret the sign.
 *
 * `fsdStamped` is the set of wall ids whose `frontFacesSameDirection` was
 * (re)stamped earlier in THIS pass. Returns false (and logs) only on an
 * intra-pass conflict — a wall genuinely shared across two rooms in the same
 * pass whose ends want opposite conventions. A *stale* stamp from a previous
 * pass must NOT block the write (otherwise a re-adjust — e.g. a vertex move
 * that flips a wall — clears the old miter but can never re-create it). We'd
 * still need a per-end front-side stamp to fully handle shared-wall conflicts.
 */
function writeMiterAtEnd(wall, end, miter, facesSameDirection, fsdStamped) {
  if (
    fsdStamped.has(wall.id) &&
    wall.frontFacesSameDirection !== facesSameDirection
  ) {
    console.warn(
      `[adjustStructureWallCorners] wall ${wall.id} has a miter from ` +
        `another room with opposite facesSameDirection; skipping miter ` +
        `at this end.`
    );
    return false;
  }
  wall.frontFacesSameDirection = facesSameDirection;
  fsdStamped.add(wall.id);
  if (end === 'BR') wall.rightMiter = miter;
  else wall.leftMiter = miter;
  return true;
}

/**
 * Clear any miter previously written at this wall end. corner-adjustment only
 * ever SETS miters, so on a re-adjust (e.g. after a vertex move turns an
 * oblique corner square) a corner that stopped being oblique would otherwise
 * keep a stale, now-wrong miter — which the 3D mitred solid and the 2D
 * footprint render as a skewed end. Each processed end is cleared up front; the
 * oblique branch re-writes when it still applies, every other branch leaves it
 * null. `frontFacesSameDirection` is a per-wall front-side stamp and is left
 * as-is (harmless with both miters null; the writeMiterAtEnd guard still holds).
 */
function clearMiterAtEnd(wall, end) {
  if (end === 'BR') wall.rightMiter = null;
  else wall.leftMiter = null;
}

/**
 * Degree-2 processing. Picks which wall "ends at" the corner and
 * which adjusts (extends past / falls short / also ends at),
 * depending on the corner angle class.
 */
function processDegree2(
  polygonCorner,
  rightSw,
  leftSw,
  angleClass,
  openings,
  stats,
  room,
  fsdStamped
) {
  const rightEnd = nearestWallEnd(rightSw, polygonCorner);
  const leftEnd = nearestWallEnd(leftSw, polygonCorner);

  if (angleClass === 'oblique') {
    applyEndsAtCorner(rightSw, rightEnd, polygonCorner, openings);
    applyEndsAtCorner(leftSw, leftEnd, polygonCorner, openings);
    tryWriteObliqueMiters(
      room,
      rightSw,
      rightEnd,
      leftSw,
      leftEnd,
      polygonCorner,
      stats,
      fsdStamped
    );
    stats.bothEndsAligned++;
    return;
  }

  // Right-angle or reflex: one wall "ends at" the polygon corner,
  // the other extends past or falls short. Prefer the wall that's
  // already aligned.
  let endsWall = rightSw;
  let endsEnd = rightEnd;
  let adjustWall = leftSw;
  let adjustEnd = leftEnd;
  if (isEndAlreadyAtCorner(rightSw, rightEnd, polygonCorner)) {
    // keep default
  } else if (isEndAlreadyAtCorner(leftSw, leftEnd, polygonCorner)) {
    endsWall = leftSw;
    endsEnd = leftEnd;
    adjustWall = rightSw;
    adjustEnd = rightEnd;
  }

  // Back-to-back composite guard: at a back-to-back wall junction
  // (Montrose composite + partition tail), a room's polygon corner can
  // sit on the composite's surface where the composite's centerline
  // stops short of `adjustWall`'s centerline. Skip in that case so a
  // different room's corner whose perpendicular wall DOES reach can
  // adjust `adjustWall`. The composite flag scopes this to back-to-back
  // assemblies; in single-scan/non-composite contexts the legacy
  // behaviour applies unchanged.
  if (endsWall._backToBackComposite || adjustWall._backToBackComposite) {
    const adjustDir = getWallDirection(adjustWall);
    const endsDir = getWallDirection(endsWall);
    const det = endsDir.x * adjustDir.z - adjustDir.x * endsDir.z;
    if (Math.abs(det) > 1e-6) {
      const dx = endsWall.center.x - adjustWall.center.x;
      const dz = endsWall.center.z - adjustWall.center.z;
      const s = (adjustDir.x * dz - adjustDir.z * dx) / det;
      const endsHalfLen = (endsWall.dimensions?.x || 0) / 2;
      if (Math.abs(s) > endsHalfLen + 0.01) {
        return;
      }
    }
  }

  applyEndsAtCorner(endsWall, endsEnd, polygonCorner, openings);

  // right-angle (≈ 90°, rectangular room corner): extend past by one
  //   thickness so the corner material is filled.
  // reflex (≈ 270°, L-elbow): shrink away by one thickness so the
  //   two walls don't overlap past the elbow tip.
  const thicknessOfEndsWall = endsWall.dimensions?.z || DEFAULT_WALL_THICKNESS;
  const signedOffset =
    angleClass === 'right-angle' ? +thicknessOfEndsWall : -thicknessOfEndsWall;
  applyOffsetPastCorner(
    adjustWall,
    adjustEnd,
    polygonCorner,
    signedOffset,
    openings
  );

  if (angleClass === 'right-angle') stats.rightAngleExtended++;
  else stats.reflexShrunk++;
}

/**
 * Degree-3 processing — classic T-junction case. One continuing wall
 * (two graph edges) + one stem (single edge). The stem's near-end is
 * aligned with the polygon corner. The continuing wall is left alone.
 */
function processDegree3Stem(polygonCorner, stemWall, openings, stats) {
  const stemEnd = nearestWallEnd(stemWall, polygonCorner);
  applyEndsAtCorner(stemWall, stemEnd, polygonCorner, openings);
  stats.tJunctionStemAligned++;
}

/**
 * Degree-3 processing — three distinct walls meeting at a T-junction
 * (no single wall has two graph edges; instead, two collinear walls
 * meet a perpendicular stem). Aligns the stem's near-end to the
 * polygon corner, then joins the two collinear walls at ONE face of
 * the stem: one wall extends to that face from its side, the other
 * retreats to that face from its side — no overlap, no gap, and
 * neither wall's rectangle intrudes into the other room's space.
 */
// dot tolerance for "perpendicular to stem". |dot| < this means the
// wall is within ~3° of perpendicular to the stem. The extender/
// stopper math projects the stem center onto the wall's centerline,
// which only equals the actual stem-crossing point when the wall is
// genuinely perpendicular. For tilted walls the projection lands far
// from the crossing and the move pushes the wall past its own
// opposite endpoint — see processTJunction3Distinct.
const PERP_TO_STEM_DOT_TOL = 0.05;

function processTJunction3Distinct(
  polygonCorner,
  stem,
  collA,
  collB,
  openings,
  stats
) {
  const stemDir = getWallDirection(stem);
  const isPerpToStem = (wall) => {
    const wDir = getWallDirection(wall);
    return (
      Math.abs(wDir.x * stemDir.x + wDir.z * stemDir.z) < PERP_TO_STEM_DOT_TOL
    );
  };

  // Back-to-back collinear pair case: when one collinear wall's
  // centerline span doesn't reach the stem's centerline (e.g.,
  // Montrose composite/tail meeting the horizontal wall where the
  // composite's z-range stops short of the stem's z), the room
  // polygon corners can sit on either wall's surface depending on
  // iteration order. The stem should align with whichever wall's
  // centerline ACTUALLY reaches it. Compute that here, ignoring the
  // polygon corner for the stem's target (it can sit on the
  // non-reaching wall's surface).
  function reachesStem(coll) {
    const collDir = getWallDirection(coll);
    const det = collDir.x * stemDir.z - stemDir.x * collDir.z;
    if (Math.abs(det) < 1e-6) return null;
    const dx = coll.center.x - stem.center.x;
    const dz = coll.center.z - stem.center.z;
    const s = (stemDir.x * dz - stemDir.z * dx) / det;
    const collHalfLen = (coll.dimensions?.x || 0) / 2;
    if (Math.abs(s) > collHalfLen + 0.01) return null;
    return { s, det, dx, dz };
  }
  // Only apply the back-to-back heuristic when one of the collinear
  // walls is a `_backToBackComposite` (positioned by
  // `combineBackToBackWalls`). In single-scan structures the 3-distinct
  // T case represents a legitimate L+stem junction where both
  // "collinear" walls truly reach the stem — picking by reach alone
  // would mis-classify whichever is marginally shorter and shrink the
  // other wall incorrectly. The composite flag is the only reliable
  // signal that this is the back-to-back partner case.
  const aReach =
    isPerpToStem(collA) &&
    (collA._backToBackComposite || collB._backToBackComposite)
      ? reachesStem(collA)
      : null;
  const bReach =
    isPerpToStem(collB) &&
    (collA._backToBackComposite || collB._backToBackComposite)
      ? reachesStem(collB)
      : null;

  // When exactly one reaches, use it for the stem target. When both
  // reach (classic 3-distinct T) or neither, fall back to the polygon
  // corner.
  let stemTargetCorner = polygonCorner;
  let nonReaching = null;
  if (aReach && !bReach) {
    nonReaching = collB;
    const collDir = getWallDirection(collA);
    const det = collDir.x * stemDir.z - stemDir.x * collDir.z;
    const dx = collA.center.x - stem.center.x;
    const dz = collA.center.z - stem.center.z;
    const t = (collDir.x * dz - collDir.z * dx) / det;
    const intersectX = stem.center.x + t * stemDir.x;
    const intersectZ = stem.center.z + t * stemDir.z;
    const ht = (collA.dimensions?.z || DEFAULT_WALL_THICKNESS) / 2;
    const sign = t > 0 ? -1 : 1; // toward stem center
    stemTargetCorner = {
      x: intersectX + ht * stemDir.x * sign,
      y: polygonCorner.y,
      z: intersectZ + ht * stemDir.z * sign,
    };
  } else if (bReach && !aReach) {
    nonReaching = collA;
    const collDir = getWallDirection(collB);
    const det = collDir.x * stemDir.z - stemDir.x * collDir.z;
    const dx = collB.center.x - stem.center.x;
    const dz = collB.center.z - stem.center.z;
    const t = (collDir.x * dz - collDir.z * dx) / det;
    const intersectX = stem.center.x + t * stemDir.x;
    const intersectZ = stem.center.z + t * stemDir.z;
    const ht = (collB.dimensions?.z || DEFAULT_WALL_THICKNESS) / 2;
    const sign = t > 0 ? -1 : 1;
    stemTargetCorner = {
      x: intersectX + ht * stemDir.x * sign,
      y: polygonCorner.y,
      z: intersectZ + ht * stemDir.z * sign,
    };
  }

  // Stem: near-end centerline = projection of computed target.
  const stemEnd = nearestWallEnd(stem, polygonCorner);
  applyEndsAtCorner(stem, stemEnd, stemTargetCorner, openings);

  // Pick extender by perpendicularity to stem first (more reliable
  // than lexical id ordering when the two "collinear" walls aren't
  // both perpendicular). The non-perpendicular wall is left alone
  // entirely — projecting the stem center onto its tilted centerline
  // produces a wildly off target.
  const aPerp = isPerpToStem(collA);
  const bPerp = isPerpToStem(collB);
  let extender, stopper;
  if (aPerp && !bPerp) {
    extender = collA;
    stopper = collB;
  } else if (bPerp && !aPerp) {
    extender = collB;
    stopper = collA;
  } else {
    // Both perpendicular (true T) or neither — fall back to id ordering.
    extender = collA.id < collB.id ? collA : collB;
    stopper = extender === collA ? collB : collA;
  }
  const halfThickStem = (stem.dimensions?.z || DEFAULT_WALL_THICKNESS) / 2;

  // Extender: move its near-end to stem_center + halfThick * outward.
  // Skip if the extender isn't perpendicular to the stem, or if it
  // doesn't reach the stem's centerline (back-to-back partner case).
  if (isPerpToStem(extender) && extender !== nonReaching) {
    const extEnd = nearestWallEnd(extender, polygonCorner);
    const extDir = getWallDirection(extender);
    const extOutward =
      extEnd === 'BL'
        ? { x: -extDir.x, y: -extDir.y, z: -extDir.z }
        : { x: extDir.x, y: extDir.y, z: extDir.z };
    const stemProjExt = projectOntoCenterline(stem.center, extender);
    const extTarget = {
      x: stemProjExt.x + halfThickStem * extOutward.x,
      y: stemProjExt.y,
      z: stemProjExt.z + halfThickStem * extOutward.z,
    };
    moveWallEndAlong(extender, extEnd, extTarget, openings);
  }

  // Stopper: move its near-end to the SAME face — stem_center in the
  // anti-stopOutward direction. Since stopOutward = −extOutward for
  // walls on opposite sides of the stem, both targets collapse to the
  // same world-space point. Same perpendicularity guard as extender,
  // plus the back-to-back non-reaching check.
  if (isPerpToStem(stopper) && stopper !== nonReaching) {
    const stopEnd = nearestWallEnd(stopper, polygonCorner);
    const stopDir = getWallDirection(stopper);
    const stopOutward =
      stopEnd === 'BL'
        ? { x: -stopDir.x, y: -stopDir.y, z: -stopDir.z }
        : { x: stopDir.x, y: stopDir.y, z: stopDir.z };
    const stemProjStop = projectOntoCenterline(stem.center, stopper);
    const stopTarget = {
      x: stemProjStop.x - halfThickStem * stopOutward.x,
      y: stemProjStop.y,
      z: stemProjStop.z - halfThickStem * stopOutward.z,
    };
    moveWallEndAlong(stopper, stopEnd, stopTarget, openings);
  }

  stats.tJunction3DistinctAligned = (stats.tJunction3DistinctAligned || 0) + 1;
}

/**
 * Given the three walls at a degree-3 node, find the perpendicular
 * "stem" and the two collinear walls. Returns null if no pair of the
 * three is collinear (direction-parallel).
 */
function classify3DistinctTJunction(walls) {
  if (!Array.isArray(walls) || walls.length !== 3) return null;
  for (let i = 0; i < 3; i++) {
    for (let j = i + 1; j < 3; j++) {
      const dI = getWallDirection(walls[i]);
      const dJ = getWallDirection(walls[j]);
      const dot = dI.x * dJ.x + dI.z * dJ.z;
      if (Math.abs(Math.abs(dot) - 1) < 0.05) {
        const k = 3 - i - j;
        return { stem: walls[k], collA: walls[i], collB: walls[j] };
      }
    }
  }
  return null;
}

// ============================================================================
// Orchestrator
// ============================================================================

/**
 * Walk each room's polygon corners in order. For each corner, look up
 * the corresponding graph node; if not already processed, apply the
 * degree-based rule and mark processed.
 *
 * @param {Array} rooms - Sub-rooms with floor.polygonCorners +
 *                         floor.edgeStructureWallMapping.
 * @param {Object} structure - { walls, doors, windows, openings }.
 * @param {Object} graph - The CV polygon-stage graph: `{ nodes, edges }`
 *                         returned from `buildRoomPolygons` as
 *                         `cvResult.graph`.
 * @returns {Object} Stats for checkpoint / debug.
 */
function adjustStructureWallCorners(rooms, structure, graph) {
  const structureWalls = structure.walls || [];
  const openings = {
    doors: structure.doors,
    windows: structure.windows,
    openings: structure.openings,
  };
  const wallById = new Map(structureWalls.map((w) => [w.id, w]));
  const stats = {
    cornersExamined: 0,
    skippedNoGraphNode: 0,
    skippedDegree1: 0,
    skippedDegree4Plus: 0,
    skippedSameWall: 0,
    skippedStraightThroughView: 0,
    rightAngleExtended: 0,
    reflexShrunk: 0,
    bothEndsAligned: 0,
    miteredCorners: 0,
    tJunctionStemAligned: 0,
  };
  // Wall ids whose frontFacesSameDirection was (re)stamped during THIS pass.
  // The writeMiterAtEnd guard consults it so it only catches genuine intra-pass
  // shared-wall conflicts — a stale stamp from a prior adjust never blocks a
  // re-adjust (e.g. re-mitring after a vertex move).
  const fsdStamped = new Set();

  if (!graph) {
    console.warn(
      '[adjustStructureWallCorners] No graph provided; skipping (the ' +
        'step now requires the CV polygon-stage graph).'
    );
    return stats;
  }

  const nodesByWall = buildNodesByWall(graph);
  const processedNodes = new Set();

  for (const room of rooms) {
    const floor = room.floors?.[0];
    if (!floor?.polygonCorners?.length) continue;
    if (!floor.edgeStructureWallMapping) continue;

    const polygon = floor.polygonCorners;
    const esm = floor.edgeStructureWallMapping;
    const N = polygon.length;

    for (let i = 0; i < N; i++) {
      const rightEdgeIdx = (i - 1 + N) % N;
      const leftEdgeIdx = i;
      const rightSwIds = esm[rightEdgeIdx];
      const leftSwIds = esm[leftEdgeIdx];
      if (!rightSwIds?.length || !leftSwIds?.length) continue;

      const polygonCorner = polygon[i];

      // When a polygon edge is backed by multiple structure walls
      // (mergeCollinearRoomWalls absorbed shorter collinear walls
      // into the merged room wall — e.g. a short stub at one end of
      // a longer wall), the wall whose endpoint actually sits at
      // this corner may not be the first id in the list. Pick the
      // candidate whose nearest centerline endpoint is closest to
      // the polygon corner so corner-adjustment operates on the
      // wall it should and findSharedNode finds the corner's graph
      // node.
      const rightWallId = pickWallAtCorner(rightSwIds, wallById, polygonCorner);
      const leftWallId = pickWallAtCorner(leftSwIds, wallById, polygonCorner);
      if (!rightWallId || !leftWallId) continue;

      // Straight-through view at a T-junction: both polygon edges
      // reference the same continuing wall's id. Skip — the
      // concave-stem polygon will handle the node.
      if (rightWallId === leftWallId) {
        stats.skippedSameWall++;
        continue;
      }

      const rightSw = wallById.get(rightWallId);
      const leftSw = wallById.get(leftWallId);
      if (!rightSw || !leftSw) continue;

      const nodeIdx = findSharedNode(nodesByWall, rightWallId, leftWallId);
      if (nodeIdx == null) {
        stats.skippedNoGraphNode++;
        continue;
      }
      if (processedNodes.has(nodeIdx)) continue;

      stats.cornersExamined++;

      // Re-adjust hygiene: drop any stale miter at the ends meeting here before
      // re-classifying, so a corner that is no longer oblique doesn't keep a
      // wrong miter (see clearMiterAtEnd). Scoped to the ends actually being
      // (re)processed — a wall shared with a non-edited room keeps its other
      // end's miter, since that corner isn't visited this pass.
      clearMiterAtEnd(rightSw, nearestWallEnd(rightSw, polygonCorner));
      clearMiterAtEnd(leftSw, nearestWallEnd(leftSw, polygonCorner));

      const degree = graph.nodes[nodeIdx].edgeIndices.length;

      if (degree === 1) {
        stats.skippedDegree1++;
        processedNodes.add(nodeIdx);
        continue;
      }
      if (degree >= 4) {
        stats.skippedDegree4Plus++;
        processedNodes.add(nodeIdx);
        continue;
      }

      if (degree === 2) {
        const angleClass = classifyCorner(polygon, i);
        processDegree2(
          polygonCorner,
          rightSw,
          leftSw,
          angleClass,
          openings,
          stats,
          room,
          fsdStamped
        );
        processedNodes.add(nodeIdx);
        continue;
      }

      // degree === 3: T-junction. Two shapes possible:
      //   (a) Classic: one wall continues through (count=2) + one stem
      //       (count=1). Process the stem; leave the continuing wall.
      //   (b) Three distinct walls (each count=1) — two collinear + one
      //       perpendicular stem. Align the stem at the polygon corner
      //       and extend one of the collinear walls across the stem's
      //       width so they span continuously.
      const perWall = edgesPerWallAtNode(graph, nodeIdx);
      if (perWall.size === 3) {
        const walls3 = Array.from(perWall.keys())
          .map((wid) => wallById.get(wid))
          .filter(Boolean);
        const classified = classify3DistinctTJunction(walls3);
        if (classified) {
          processTJunction3Distinct(
            polygonCorner,
            classified.stem,
            classified.collA,
            classified.collB,
            openings,
            stats
          );
          processedNodes.add(nodeIdx);
          continue;
        }
      }
      let stemWallId = null;
      for (const [wid, count] of perWall) {
        if (count === 1) {
          stemWallId = wid;
          break;
        }
      }
      if (!stemWallId) {
        // Degenerate: no clear stem (e.g., three different walls each
        // with one edge). Skip.
        stats.skippedNoGraphNode++;
        processedNodes.add(nodeIdx);
        continue;
      }
      const stemWall = wallById.get(stemWallId);
      if (stemWall)
        processDegree3Stem(polygonCorner, stemWall, openings, stats);
      processedNodes.add(nodeIdx);
    }
  }

  return stats;
}

// ============================================================================
// Wall-height corner snap (Step A of the soffit plan)
// ============================================================================
//
// After `adjustStructureWallCorners` aligns wall XZ corners, walls
// that meet at a corner can still carry slightly different top-Y
// values (capture noise, multi-scan registration, manual edits). This
// pass snaps them together so downstream ceiling generation sees one
// height per node, instead of treating mm-level noise as architectural
// height transitions.
//
// Snap is **up only** and **per-corner-local**: for each graph node,
// we compute `top_target = max(top_Y_at_node(w) for w meeting w)` and
// each wall below that target is a candidate to lift, gated by wall
// representation:
//   - Rectangular: lift the whole top edge (5 cm tolerance, considers
//     both ends).
//   - polygonCorners: lift only the one polygon-corner Y at this end,
//     gated by the proportion of the top boundary displaced by more
//     than 10 cm (≤ 75% allowed; closed-form cap of ~40 cm Δ).
//
// The pass writes only `wall.center.y`, `wall.dimensions.y`, and
// individual `polygonCorners[i].y`. All XZ geometry from the prior
// alignment step is preserved.

/**
 * Does the wall have a polygonCorners representation worth interpreting
 * as a slope/gable/notch?
 */
function hasPolygonCorners(wall) {
  return Array.isArray(wall.polygonCorners) && wall.polygonCorners.length >= 3;
}

/**
 * For a polygonCorners wall, find the polygon-corner index of the "top
 * vertex at the requested wall end" (BL or BR). The top vertex at an
 * end is the polygon corner with the most-extreme projection along the
 * wall's direction, among the corners of the outline's TOP edge — found by
 * the outline's shape (`wallOutlineEdges.outlineEdges`), not by height. The
 * height rule ("above minY + 0.1 m") read a stepped BOTTOM's upper corners as
 * the top: on `6F51BDA5`'s east wall, stepped 0.41 m down behind a sunken
 * nook, it took a bottom corner for the kitchen end's top and never snapped
 * the real one. It stays the fallback for an outline whose sides the walk
 * cannot find.
 *
 * For 3-corner polygons (triangular gables), the peak is the single
 * vertex above the floor edge — both ends share it.
 *
 * Returns the index into `wall.polygonCorners`, or -1 if no top vertex
 * could be identified.
 */
function topVertexIndexAtEnd(wall, end) {
  const pc = wall.polygonCorners;
  if (!Array.isArray(pc) || pc.length < 3) return -1;

  const ys = pc.map((p) => p.y);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  if (maxY - minY < 0.1) return -1; // wall too flat in Y to identify a top

  // 3-corner triangular gable — single peak shared by both ends.
  if (pc.length === 3) {
    let peakIdx = -1;
    let peakY = -Infinity;
    for (let i = 0; i < pc.length; i++) {
      if (pc[i].y > peakY) {
        peakY = pc[i].y;
        peakIdx = i;
      }
    }
    return peakIdx;
  }

  const dir = getWallDirection(wall);
  const origin = wall.center || pc[0];
  const { top } = outlineEdges(pc, { origin, ux: dir.x, uz: dir.z });
  let bestIdx = -1;
  let bestProj = end === 'BL' ? Infinity : -Infinity;
  for (const i of top) {
    const proj = (pc[i].x - origin.x) * dir.x + (pc[i].z - origin.z) * dir.z;
    if (end === 'BL' ? proj < bestProj : proj > bestProj) {
      bestProj = proj;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Local top Y of `wall` at the given end (BL/BR). For rectangular
 * walls this equals `center.y + dimensions.y/2` (uniform). For
 * polygonCorners walls it's the Y of the top vertex nearest that end.
 * Returns null if no top can be derived.
 */
function topYAtEnd(wall, end) {
  if (hasPolygonCorners(wall)) {
    const idx = topVertexIndexAtEnd(wall, end);
    if (idx < 0) return null;
    return wall.polygonCorners[idx].y;
  }
  if (!wall.dimensions || !wall.center) return null;
  return (wall.center.y || 0) + (wall.dimensions.y || 0) / 2;
}

/**
 * For each graph node touched by a room polygon corner, compute the
 * **target top Y** = max of each meeting wall's local top at that node.
 * Same orchestration as `adjustStructureWallCorners` — walks each
 * room's polygon corners and resolves them to graph nodes — so the
 * snap only fires at corners that bound a real interior room. Nodes
 * that exist only on the exterior virtual room or in graph-only
 * intersections are intentionally skipped; ceilings live inside rooms.
 *
 * Returns `Map<nodeIdx, { target, polygonCorner }>`.
 */
function computeTopTargetsByNode(rooms, graph, wallById) {
  const nodesByWall = buildNodesByWall(graph);
  const targetByNode = new Map();
  if (!rooms?.length) return { targetByNode, nodesByWall };

  for (const room of rooms) {
    const floor = room.floors?.[0];
    if (!floor?.polygonCorners?.length || !floor.edgeStructureWallMapping)
      continue;
    const polygon = floor.polygonCorners;
    const esm = floor.edgeStructureWallMapping;
    const N = polygon.length;

    for (let i = 0; i < N; i++) {
      const rightSwIds = esm[(i - 1 + N) % N];
      const leftSwIds = esm[i];
      if (!rightSwIds?.length || !leftSwIds?.length) continue;
      const polygonCorner = polygon[i];

      const rightWallId = pickWallAtCorner(rightSwIds, wallById, polygonCorner);
      const leftWallId = pickWallAtCorner(leftSwIds, wallById, polygonCorner);
      if (!rightWallId || !leftWallId || rightWallId === leftWallId) continue;

      const nodeIdx = findSharedNode(nodesByWall, rightWallId, leftWallId);
      if (nodeIdx == null) continue;
      if (targetByNode.has(nodeIdx)) continue;

      // Collect every wall touching the node — not just the two
      // adjacent polygon-edge walls — so degree-3 / degree-4 joins
      // (T-junctions, cross-junctions) see all heights.
      const wallIdsAtNode = new Set();
      for (const ei of graph.nodes[nodeIdx].edgeIndices) {
        wallIdsAtNode.add(graph.edges[ei].wallId);
      }

      let target = -Infinity;
      for (const wid of wallIdsAtNode) {
        const w = wallById.get(wid);
        if (!w) continue;
        const end = nearestWallEnd(w, polygonCorner);
        const ty = topYAtEnd(w, end);
        if (ty != null && ty > target) target = ty;
      }
      if (Number.isFinite(target)) {
        targetByNode.set(nodeIdx, { target, polygonCorner });
      }
    }
  }
  return { targetByNode, nodesByWall };
}

/**
 * Find which of `wall`'s incident graph nodes correspond to its BL and
 * BR centerline endpoints. Returns `{ BL: nodeIdx|null, BR: nodeIdx|null }`.
 *
 * Graph nodes sit at wall *surface* intersections, offset from the
 * wall's centerline by ~half-thickness perpendicular to the wall, so
 * a direct XZ-distance match against `calculateWallCorners` would
 * miss by ~0.06 m. Instead, project every incident node onto the
 * wall's direction (perpendicular offsets contribute zero) and pick
 * the two extremes — smallest projection = BL, largest = BR. Robust
 * against T-junction split nodes mid-wall and against the surface
 * offset.
 */
function endpointNodesForWall(wall, nodesByWall, graph) {
  const nodeIds = nodesByWall.get(wall.id);
  if (!nodeIds || nodeIds.size === 0) return { BL: null, BR: null };

  const dir = getWallDirection(wall);
  const origin = wall.center || { x: 0, z: 0 };

  let blNode = null;
  let brNode = null;
  let blProj = Infinity;
  let brProj = -Infinity;
  for (const nodeIdx of nodeIds) {
    const n = graph.nodes[nodeIdx];
    if (!n) continue;
    const proj = (n.x - origin.x) * dir.x + (n.z - origin.z) * dir.z;
    if (proj < blProj) {
      blProj = proj;
      blNode = nodeIdx;
    }
    if (proj > brProj) {
      brProj = proj;
      brNode = nodeIdx;
    }
  }
  // Degenerate: only one incident node found. Treat as the wall having
  // no usable BL/BR pair — skip the snap for this wall.
  if (blNode === brNode) return { BL: blNode, BR: null };
  return { BL: blNode, BR: brNode };
}

/**
 * Apply the polygonCorners 75%/10cm gate at one end of one wall.
 * Updates `polygonCorners[targetIdx].y` to the target if the gate
 * accepts. Returns `{ accepted, delta, displacedProportion }`.
 *
 * Gate: a lift is first hard-capped at POLY_SNAP_MAX_DELTA (0.15 m) —
 * any larger Δ is rejected outright with a null displaced proportion
 * (commit 0a5611c8, "cap lift at 15 cm"). Within that cap the older
 * displaced-proportion check still applies: snapping one top corner by Δ
 * while the other stays put produces linear-decay displacement along the
 * top boundary (Δ at the snapped end, 0 at the other); the proportion
 * displaced by more than POLY_NEAR_LIMIT is max(0, 1 − POLY_NEAR_LIMIT/Δ),
 * accepted iff ≤ POLY_DISPLACED_PROPORTION_MAX. The 0.15 m hard cap is
 * stricter than that proportion bound would allow (it caps at 0.40 m), so
 * the proportion gate never fires in practice — kept for defence in depth.
 */
function trySnapPolygonCornerAtEnd(wall, end, targetY) {
  const idx = topVertexIndexAtEnd(wall, end);
  if (idx < 0) return null;
  // Corners flagged `_regionAssigned` are owned by the ceiling
  // region-fit / nudge phases; snap must not clobber them.
  if (wall.polygonCorners[idx]._regionAssigned) return null;
  const currentY = wall.polygonCorners[idx].y;
  const delta = targetY - currentY;
  if (delta <= PRECISION_EPS) return null;
  if (delta > POLY_SNAP_MAX_DELTA) {
    return { accepted: false, delta, displacedProportion: null };
  }
  const displaced = delta > POLY_NEAR_LIMIT ? 1 - POLY_NEAR_LIMIT / delta : 0;
  if (displaced > POLY_DISPLACED_PROPORTION_MAX + 1e-9) {
    return { accepted: false, delta, displacedProportion: displaced };
  }
  wall.polygonCorners[idx].y = targetY;
  return { accepted: true, delta, displacedProportion: displaced };
}

/**
 * Apply the rectangular snap rule. Considers both ends' targets at
 * once: accept the per-end Δs that fall in (0, SNAP_TOL_RECT]; lift by
 * the max accepted Δ. Returns `{ delta, asymmetric }` if any lift
 * happened, else null.
 */
function trySnapRectangularWall(wall, blTarget, brTarget) {
  const currentTop = (wall.center?.y || 0) + (wall.dimensions?.y || 0) / 2;
  const deltas = [];
  for (const t of [blTarget, brTarget]) {
    if (t == null) continue;
    const d = t - currentTop;
    if (d > PRECISION_EPS && d <= SNAP_TOL_RECT + 1e-9) deltas.push(d);
  }
  if (deltas.length === 0) return null;
  const maxDelta = Math.max(...deltas);
  const asymmetric =
    deltas.length === 2 && Math.abs(deltas[0] - deltas[1]) > PRECISION_EPS;
  wall.center.y += maxDelta / 2;
  wall.dimensions.y += maxDelta;
  return { delta: maxDelta, asymmetric };
}

/**
 * Look up the graph node at `xz` within `tolerance`, or null if none.
 * Used to map a room wall / exterior wall's `alignment.bottomLeft` or
 * `bottomRight` (which sit at polygon corners = graph nodes by
 * construction) to the corresponding node id without going through the
 * structure-wall id index.
 */
function findGraphNodeAtXZ(xz, graph, tolerance = 0.05) {
  if (!graph?.nodes || !xz) return null;
  let bestIdx = null;
  let bestDist = tolerance;
  for (let i = 0; i < graph.nodes.length; i++) {
    const n = graph.nodes[i];
    if (!n) continue;
    const d = Math.hypot(n.x - xz.x, n.z - xz.z);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Apply the same per-end snap (`trySnapPolygonCornerAtEnd` /
 * `trySnapRectangularWall`) to one room wall or exterior wall, given a
 * target Y at each of its alignment endpoints. Updates per-wall stats
 * in `stats`. Used to keep room walls and exterior walls aligned with
 * the structure-wall snap result at shared corners — otherwise the
 * structure wall would lift to a tall neighbor's top while the
 * matching room / exterior wall stayed at its lower input value.
 */
function snapOneAlignmentWall(wall, blTarget, brTarget, stats) {
  if (blTarget == null && brTarget == null) return;
  if (hasPolygonCorners(wall)) {
    for (const [end, t] of [
      ['BL', blTarget],
      ['BR', brTarget],
    ]) {
      if (t == null) continue;
      const result = trySnapPolygonCornerAtEnd(wall, end, t);
      if (!result) continue;
      if (result.accepted) {
        stats.polyCornersSnapped++;
        if (result.delta > stats.worstPolyDelta)
          stats.worstPolyDelta = result.delta;
        if (result.displacedProportion > stats.worstPolyDisplacedProportion)
          stats.worstPolyDisplacedProportion = result.displacedProportion;
      } else {
        stats.polyCornersRejected++;
        if (result.delta > stats.worstRejectedPolyDelta)
          stats.worstRejectedPolyDelta = result.delta;
      }
    }
    return;
  }
  const result = trySnapRectangularWall(wall, blTarget, brTarget);
  if (!result) return;
  stats.rectWallsSnapped++;
  if (result.delta > stats.worstRectDelta) stats.worstRectDelta = result.delta;
  if (result.asymmetric) stats.rectWallsAsymmetric++;
}

/**
 * Snap each meeting wall's top Y at every graph node to the local max,
 * subject to per-type gates. Pure write to `wall.center.y`,
 * `wall.dimensions.y`, and `polygonCorners[i].y` only — XZ is left
 * untouched.
 *
 * Applies to: structure walls, every room wall in `rooms`, and (when
 * present) the exterior virtual room's walls. All three sets are
 * snapped to the same per-node target so a partition wall's interior,
 * its parent structure wall, and the building's outer face don't
 * disagree about height at a shared corner. The local-max target is
 * still computed from the structure-wall set only — room and exterior
 * walls inherit from those, so their heights are downstream of the
 * structure-wall values and shouldn't pull the target up.
 *
 * @param {Array} rooms - Sub-rooms; each room's `walls` are snapped.
 * @param {Object} structure - { walls, exterior?: { walls } } — both
 *   sets are snapped. `walls` is also used to compute the per-node
 *   target.
 * @param {Object} graph - The CV polygon-stage graph { nodes, edges }.
 * @returns {Object} Stats (counts + worst-case deltas for both wall types).
 */
function snapWallHeightsAtCorners(rooms, structure, graph) {
  const stats = {
    nodesEvaluated: 0,
    rectWallsSnapped: 0,
    rectWallsAsymmetric: 0,
    polyCornersSnapped: 0,
    polyCornersRejected: 0,
    worstRectDelta: 0,
    worstPolyDelta: 0,
    worstPolyDisplacedProportion: 0,
    worstRejectedPolyDelta: 0,
    skippedNoGraph: 0,
  };

  if (!graph) {
    console.warn(
      '[snapWallHeightsAtCorners] No graph provided; skipping (the step ' +
        'requires the CV polygon-stage graph).'
    );
    stats.skippedNoGraph = 1;
    return stats;
  }

  const structureWalls = structure.walls || [];
  const wallById = new Map(structureWalls.map((w) => [w.id, w]));

  const { targetByNode, nodesByWall } = computeTopTargetsByNode(
    rooms,
    graph,
    wallById
  );
  stats.nodesEvaluated = targetByNode.size;

  for (const wall of structureWalls) {
    const { BL: blNode, BR: brNode } = endpointNodesForWall(
      wall,
      nodesByWall,
      graph
    );
    const blEntry = blNode != null ? targetByNode.get(blNode) : null;
    const brEntry = brNode != null ? targetByNode.get(brNode) : null;
    if (!blEntry && !brEntry) continue;

    if (hasPolygonCorners(wall)) {
      for (const [end, entry] of [
        ['BL', blEntry],
        ['BR', brEntry],
      ]) {
        if (!entry) continue;
        const result = trySnapPolygonCornerAtEnd(wall, end, entry.target);
        if (!result) continue;
        if (result.accepted) {
          stats.polyCornersSnapped++;
          if (result.delta > stats.worstPolyDelta)
            stats.worstPolyDelta = result.delta;
          if (result.displacedProportion > stats.worstPolyDisplacedProportion)
            stats.worstPolyDisplacedProportion = result.displacedProportion;
        } else {
          stats.polyCornersRejected++;
          if (result.delta > stats.worstRejectedPolyDelta)
            stats.worstRejectedPolyDelta = result.delta;
        }
      }
      continue;
    }

    const blTarget = blEntry?.target ?? null;
    const brTarget = brEntry?.target ?? null;
    const result = trySnapRectangularWall(wall, blTarget, brTarget);
    if (!result) continue;
    stats.rectWallsSnapped++;
    if (result.delta > stats.worstRectDelta)
      stats.worstRectDelta = result.delta;
    if (result.asymmetric) {
      stats.rectWallsAsymmetric++;
      console.warn(
        `[snapWallHeightsAtCorners] wall ${wall.id}: conflicting ` +
          `in-tolerance snap targets at its two ends; lifted to max.`
      );
    }
  }

  // Now snap room walls and exterior walls to the same per-node
  // target. These walls aren't part of `nodesByWall` (the graph
  // indexes structure walls), so we match them by XZ proximity to the
  // graph nodes — their `alignment.bottomLeft / .bottomRight` sit at
  // polygon corners by construction (graph nodes are exactly the CV
  // polygon corners), so a 5 cm tolerance is plenty.
  const snapAlignmentWalls = (walls) => {
    for (const wall of walls || []) {
      const bl = wall.alignment?.bottomLeft;
      const br = wall.alignment?.bottomRight;
      const blNode = findGraphNodeAtXZ(bl, graph);
      const brNode = findGraphNodeAtXZ(br, graph);
      const blEntry = blNode != null ? targetByNode.get(blNode) : null;
      const brEntry = brNode != null ? targetByNode.get(brNode) : null;
      snapOneAlignmentWall(
        wall,
        blEntry?.target ?? null,
        brEntry?.target ?? null,
        stats
      );
    }
  };
  for (const room of rooms || []) {
    snapAlignmentWalls(room.walls);
  }
  snapAlignmentWalls(structure?.exterior?.walls);

  return stats;
}

module.exports = {
  adjustStructureWallCorners,
  snapWallHeightsAtCorners,
  // The drawn-wall chain (web-app `utils/wallChain.js`) mitres its bends with
  // the same guard against a diverging back-face intersection.
  MAX_MITER_ANGLE_RAD,
  // Exported for tests
  _internal: {
    nearestWallEnd,
    classifyCorner,
    projectOntoCenterline,
    centerlineEndpoint,
    findSharedNode,
    PRECISION_EPS,
    SNAP_TOL_RECT,
    POLY_NEAR_LIMIT,
    POLY_DISPLACED_PROPORTION_MAX,
    topYAtEnd,
    topVertexIndexAtEnd,
    hasPolygonCorners,
    trySnapPolygonCornerAtEnd,
    trySnapRectangularWall,
  },
};
